import React, { useEffect, useState } from "react";
import { useHistory, Link } from "react-router-dom";
import { Button, Result, Typography } from "antd";
import usePost from "../services/usePost";
import Spinner from "./global/spinner";
import sidebarImage from "../assets/login-sidebar.png";

const { Title, Text } = Typography;

const VerifyAccount = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const email = queryParams.get("email");
  const code = queryParams.get("code");
  const history = useHistory();
  const [verificationStatus, setVerificationStatus] = useState("verifying"); // verifying, success, error

  const { callApi: verifyAccount } = usePost({
    endpoint: `user/verifyAccount`,
  });

  useEffect(() => {
    const performVerification = async () => {
      if (!email || !code) {
        setVerificationStatus("error");
        return;
      }

      try {
        const postData = { email, code };
        const result = await verifyAccount(postData);
        if (result) {
          setVerificationStatus("success");
        } else {
          setVerificationStatus("error");
        }
      } catch (err) {
        setVerificationStatus("error");
      }
    };

    performVerification();
  }, [email, code, verifyAccount]);

  return (
    <div className="flex min-h-screen">
      {/* Left Side - Image & Slogan (Same as Login for consistency) */}
      <div className="hidden lg:flex w-1/2 relative bg-gray-900 items-center justify-center overflow-hidden">
        <img
          src={sidebarImage}
          alt="Verification Background"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Logo on Sidebar */}
        <div className="absolute top-8 left-8 z-30">
          <h1 className="text-4xl font-bold tracking-tighter m-0">
            <span className="text-primary">Leads</span>
            <span className="text-white">Import</span>
          </h1>
        </div>
      </div>

      {/* Right Side - Verification Content */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-white p-8 lg:p-16">
        <div className="w-full max-w-md space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="text-center">
            {verificationStatus === "verifying" && (
              <div className="flex flex-col items-center space-y-4">
                <Spinner size="large" className="!w-12 !h-12 border-b-4" />
                <Title level={3} className="!mt-4 font-bold text-gray-900">
                  Verifying your account...
                </Title>
                <Text className="text-gray-500">Please wait while we validate your credentials.</Text>
              </div>
            )}

            {verificationStatus === "success" && (
              <div className="animate-in zoom-in duration-500">
                <Result
                  status="success"
                  title={<span className="text-2xl font-bold text-gray-900">Account Verified!</span>}
                  subTitle={
                    <div className="space-y-4 mt-4">
                      <p className="text-gray-600">Your account has been successfully verified.</p>
                      <p className="font-semibold text-primary">Now try login with your credentials</p>
                    </div>
                  }
                  extra={[
                    <Button
                      key="login"
                      type="primary"
                      onClick={() => history.push("/")}
                      className="w-full h-12 text-base font-bold rounded-xl shadow-lg shadow-primary/25 !bg-primary hover:!bg-primary/90 border-none px-12"
                    >
                      Go to Login
                    </Button>
                  ]}
                />
              </div>
            )}

            {verificationStatus === "error" && (
              <div className="animate-in zoom-in duration-500">
                <Result
                  status="error"
                  title={<span className="text-2xl font-bold text-gray-900">Verification Failed</span>}
                  subTitle={
                    <div className="space-y-4 mt-4">
                      <p className="text-gray-600">We couldn't verify your account. The link might be expired or invalid.</p>
                    </div>
                  }
                  extra={[
                    <div className="space-y-4" key="actions">
                      <Button
                        type="primary"
                        onClick={() => window.location.reload()}
                        className="w-full h-12 text-base font-bold rounded-xl !bg-gray-800 hover:!bg-gray-700 border-none"
                      >
                        Try Again
                      </Button>
                      <p className="text-gray-500 pt-4">
                        Need help? <Link to="/signup" className="text-primary font-bold">Sign Up Again</Link>
                      </p>
                    </div>
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyAccount;

